<template>
	<v-responsive
		ref="cover"
		v-if="images.length"
		v-intersect.once="start"
		:aspect-ratio="ratio"
		class="cover"
		:class="{
			'cover--mounted': isMounted,
			'cover--ready': isReady,
			'cover--skew': settings.displayDiagonalImages,
			'cover--in': isReady && isLoaded(index) && transitionIn
		}"
		:style="styles"
	>
		<div class="cover__content">
			<transition mode="out-in" :duration="settings.imageTransitionDuration" @before-enter="transitionIn = true" @before-leave="transitionIn = false">
				<template v-for="(image, i) in images">
					<img :key="image.key" v-if="i == index" class="cover__image" :src="image.src" alt="Forsidebilde" @load="image.isLoaded = true" loading="lazy" />
				</template>
			</transition>
		</div>
	</v-responsive>
</template>

<script>
import { CoverImage, Settings } from '@/services/models/umbraco/SearchAndCoverImage';
const { width, height } = new CoverImage();

export default {
	name: 'cover-image-sequence',
	props: {
		images: {
			type: Array[CoverImage],
			default: () => []
		},
		settings: {
			type: Settings,
			default: () => new Settings()
		}
	},
	data: () => ({
		index: 0,
		isMounted: false,
		isReady: false,
		ratio: width / height,
		transitionIn: true
	}),
	computed: {
		styles() {
			return {
				'--transition-duration': `${this.settings.displayAnimations ? this.settings.imageTransitionDuration : 0}ms`
			};
		}
	},
	methods: {
		async start() {
			if (this.isMounted) return false;

			// Mount
			await this.delay(1000);
			this.isMounted = true;

			// Ready up
			await this.delay();
			this.isReady = true;

			// Start image sequence swapping
			await this.delay(this.settings.imageDuration);
			this.nextImage();
		},

		isLoaded(index) {
			return !!Object(this.images[index]).isLoaded;
		},

		delay(timeout = this.settings.imageTransitionDuration) {
			return new Promise(resolve => {
				this.$nextTick(() => {
					setTimeout(() => {
						requestAnimationFrame(resolve);
					}, timeout);
				});
			});
		},

		async nextImage() {
			const nextIndex = (this.index + 1) % this.images.length;

			await this.delay(0);
			this.index = nextIndex;

			// Next
			await this.delay(this.settings.imageDuration);
			this.nextImage();
		}
	}
};
</script>

<style lang="scss" scoped>
@import '~@/styles/variables';

.cover {
	position: absolute;
	width: calc(100% - 24px);

	&::after {
		content: '';
		background-color: var(--v-primary-base);
		position: absolute;
		top: 0;
		right: 0;
		bottom: 0;
		left: 0;
		width: 100%;
		transform: scaleX(0);
		transform-origin: left;
		transition: transform ease-in-out var(--transition-duration);
		will-change: transform;
	}

	&--mounted {
		&::after {
			transform: scaleX(1);
		}
	}

	&--ready {
		.cover__image {
			opacity: 1;
		}
	}

	&--in {
		&::after {
			transform-origin: right;
			transform: scaleX(0);
		}
	}

	&--skew {
		clip-path: polygon(0 25%, 100% 0%, 100% 75%, 0% 100%);
	}

	&__content {
		height: 100%;
		max-height: 100%;
		overflow: hidden;
	}

	&__image {
		display: flex;
		position: absolute;
		object-fit: cover;
		min-width: 1px;
		width: 100%;
		height: 100%;
		opacity: 0;
	}
}
</style>
